// BI Hub — Business Intelligence landing.
// Curated dashboards over the live reporting surface; every card links to a working page.
// The full parameterised cut of the books lives in the Reports Catalogue (/reports-catalogue).

import { useMemo } from 'react';
import { AppShell } from '@/components/AppShell';
import { KbdChip } from '@/components/primitives/KbdChip';
import { Money } from '@/components/primitives/Money';
import { useChaiBrief } from '@/api/reports';
import { formatDateDDMMMYYYY } from '@/utils/format';
import { cn } from '@/lib/cn';

interface Dashboard {
  id: string;
  title: string;
  category: 'revenue' | 'cash' | 'compliance' | 'operations';
  description: string;
  href: string;
  metrics: string[]; // small hint list
}

const DASHBOARDS: Dashboard[] = [
  { id: 'ceo', title: 'CEO Cockpit', category: 'revenue', description: 'Revenue · cash · outstanding · collections at a glance', href: '/bi/ceo-cockpit', metrics: ['Revenue', 'Cash', 'Outstanding', 'Growth'] },
  { id: 'sales-360', title: 'Sales 360°', category: 'revenue', description: 'Every sales invoice with party + GST split — drill to any voucher', href: '/reports/sales-register', metrics: ['Invoices', 'Party mix', 'GST split'] },
  { id: 'purchase-360', title: 'Purchase 360°', category: 'operations', description: 'Every purchase bill with vendor + GST split — drill to any voucher', href: '/reports/purchase-register', metrics: ['Vendor mix', 'GST split', 'ITC'] },
  { id: 'cash-flow', title: 'Cash Flow', category: 'cash', description: 'Schedule III cash flow — operating, investing, financing', href: '/reports/cash-flow', metrics: ['Inflows', 'Outflows', 'Net movement'] },
  { id: 'receivables', title: 'Receivables Ageing', category: 'cash', description: 'Bill-wise 0-30 · 31-60 · 61-90 · 90+ · overdue-by-party', href: '/reports/outstanding', metrics: ['0-30d', '31-60d', '61-90d', '90d+'] },
  { id: 'payables', title: 'Payables Ageing', category: 'cash', description: 'Vendor ageing + MSME 43B(h) 15/45-day tripwire', href: '/reports/msme-payables', metrics: ['MSME due', 'Aged', 'Priority'] },
  { id: 'gst-cockpit', title: 'GST Cockpit', category: 'compliance', description: 'GSTR-1 · GSTR-3B · net payable with Rule 88A ITC set-off', href: '/reports/gstr-3b', metrics: ['Outward', 'ITC', 'Net liability'] },
  { id: 'tds-tcs', title: 'TDS', category: 'compliance', description: 'Section-wise TDS deducted in books + deposit due · 206AA-aware', href: '/reports/tds-liability', metrics: ['194C', '194J', '194Q'] },
  { id: 'stock-analytics', title: 'Stock Analytics', category: 'operations', description: 'Per-item opening + in + out + closing, with reorder alerts', href: '/reports/stock-summary', metrics: ['Closing stock', 'Movement', 'Reorder'] },
  { id: 'anomaly', title: 'Anomaly Radar', category: 'compliance', description: '8-rule engine · duplicate invoices · round-tripping · off-hours entries', href: '/admin/suspicious-activity', metrics: ['Alerts', 'Severity'] },
];

const CATEGORY_META: Record<Dashboard['category'], { label: string; grad: string; accent: string }> = {
  revenue:     { label: 'Revenue',     grad: 'from-cobalt/[0.10] to-cobalt/0',  accent: 'bg-cobalt text-snow' },
  cash:        { label: 'Cash',        grad: 'from-cobalt/[0.10] to-cobalt/0',  accent: 'bg-cobalt text-snow' },
  compliance:  { label: 'Compliance',  grad: 'from-cobalt/[0.10] to-cobalt/0',  accent: 'bg-cobalt text-snow' },
  operations:  { label: 'Operations',  grad: 'from-cobalt/[0.10] to-cobalt/0',  accent: 'bg-cobalt text-snow' },
};

export function BIHubPage() {
  const brief = useChaiBrief();

  const groups = useMemo(() => {
    const m: Record<string, Dashboard[]> = {};
    for (const d of DASHBOARDS) {
      (m[d.category] ??= []).push(d);
    }
    return m;
  }, []);

  const liveCount = DASHBOARDS.length;

  return (
    <AppShell>
      {/* Hero */}
      <section className="relative overflow-hidden rounded-lg border border-mist bg-haze p-6 sm:p-8">
        <div className="absolute inset-x-0 top-0 h-1 bg-cobalt" aria-hidden />
        <div className="absolute -right-24 -top-16 h-64 w-64 rounded-full bg-cobalt/[0.05] blur-3xl" aria-hidden />
        <div className="absolute -left-16 -bottom-24 h-56 w-56 rounded-full bg-cobalt/[0.05] blur-3xl" aria-hidden />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="inline-flex items-center h-5 px-2 rounded-sm bg-cobalt/10 text-cobalt text-nano font-medium uppercase tracking-[0.12em]">Business Intelligence</span>
            </div>
            <h1 className="font-display text-display-xl font-semibold text-obsidian" style={{ letterSpacing: 'var(--tracking-display)' }}>
              Every number, one screen away.
            </h1>
            <p className="mt-2 max-w-2xl text-body-lg text-slate">
              {liveCount} curated dashboards over your live books — open any card, drill to the voucher, export.
              Need a different cut? The <a href="/reports-catalogue" className="text-cobalt underline decoration-cobalt/30 underline-offset-2 hover:decoration-cobalt">Reports Catalogue</a> has 300+ ready reports.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <MiniStat label="Dashboards" value={liveCount} />
            <MiniStat label="Reports" value="300+" />
          </div>
        </div>

        {/* Today's KPI strip */}
        <div className="orz-stagger relative mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiTile label="Cash on hand"        value={brief.data?.cashOnHand ?? 0} hint={formatDateDDMMMYYYY(new Date())} />
          <KpiTile label="Today's collections" value={brief.data?.todaysCollections ?? 0} hint="Cash + bank" />
          <KpiTile label="Receivables"         value={brief.data?.receivables ?? 0} hint="Bill-wise open" />
          <KpiTile label="Vouchers today"      value={brief.data?.vouchersToday ?? 0} hint="Open the Day Book" count />
        </div>
      </section>

      {/* Groups */}
      <div className="mt-8 flex flex-col gap-8">
        {(['revenue','cash','compliance','operations'] as const).map((cat) => {
          const items = groups[cat] ?? [];
          if (items.length === 0) return null;
          const meta = CATEGORY_META[cat];
          return (
            <section key={cat}>
              <div className="mb-3 flex items-center gap-3">
                <span className={cn('inline-flex h-6 w-6 items-center justify-center rounded-sm text-nano font-medium uppercase tracking-[0.12em]', meta.accent)}>
                  {meta.label[0]}
                </span>
                <h2 className="font-display text-display-sm font-semibold text-obsidian" style={{ letterSpacing: 'var(--tracking-display)' }}>
                  {meta.label}
                </h2>
                <span className="text-caption text-cool">{items.length} dashboard{items.length === 1 ? '' : 's'}</span>
              </div>
              <div className="orz-stagger grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((d) => <DashboardCard key={d.id} d={d} />)}
              </div>
            </section>
          );
        })}
      </div>

      <footer className="mt-10 flex flex-col items-start gap-2 border-t border-mist pt-4 text-caption text-cool sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span>Search any report from anywhere</span>
          <KbdChip>Ctrl K</KbdChip>
        </div>
        <a href="/reports-catalogue" className="font-mono text-nano text-cobalt hover:underline">Reports Catalogue → 300+ ready reports</a>
      </footer>
    </AppShell>
  );
}

function DashboardCard({ d }: { d: Dashboard }) {
  const meta = CATEGORY_META[d.category];

  return (
    <a
      href={d.href}
      className={cn(
        'group relative flex flex-col gap-2 overflow-hidden rounded-md border border-mist bg-haze p-5 transition-all duration-150 ease-out',
        'ring-1 ring-transparent',
        'hover:-translate-y-0.5 hover:shadow-card hover:border-transparent hover:ring-2 hover:ring-cobalt/30',
      )}
    >
      <div className={cn('absolute inset-x-0 top-0 h-24 -z-0 bg-gradient-to-b', meta.grad)} aria-hidden />
      <div className="relative flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={cn('inline-flex items-center h-5 px-2 rounded-sm text-nano font-medium uppercase tracking-[0.12em]', meta.accent)}>
            {meta.label}
          </span>
        </div>
        <span className="inline-flex items-center h-5 px-2 rounded-sm bg-emerald/10 text-emerald text-nano font-medium uppercase tracking-[0.12em]">
          Live
        </span>
      </div>
      <h3 className="relative font-display text-display-sm font-semibold text-obsidian" style={{ letterSpacing: 'var(--tracking-display)' }}>
        {d.title}
      </h3>
      <p className="relative text-body-sm text-slate">{d.description}</p>
      <ul className="relative mt-1 flex flex-wrap gap-1.5">
        {d.metrics.map((m) => (
          <li key={m} className="inline-flex items-center h-5 px-1.5 rounded-sm bg-frost text-cool text-nano font-mono">
            {m}
          </li>
        ))}
      </ul>
      <span className="absolute right-4 bottom-4 text-cobalt opacity-0 transition group-hover:opacity-100" aria-hidden>→</span>
    </a>
  );
}

function KpiTile({ label, value, hint, count }: { label: string; value: number; hint: string; count?: boolean }) {
  return (
    <div className={cn('orz-lift relative overflow-hidden rounded-md border border-mist bg-haze p-4 hover:border-cobalt/40')} style={{ backgroundImage: 'var(--wash-card)' }}>
      <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-cobalt/[0.10] to-transparent" aria-hidden />
      <div className="relative flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-cobalt" aria-hidden />
        <span className="text-micro font-medium uppercase tracking-[0.12em] text-cool">{label}</span>
      </div>
      {/* Whole rupees, smaller base size — the 2-col 390px grid clips paise-wide figures. */}
      <div className="relative mt-2 min-w-0 overflow-hidden text-body-lg sm:text-display-md font-semibold text-obsidian" data-numeric>
        {count
          ? <span className="font-mono tabular-nums">{value.toLocaleString('en-IN')}</span>
          : <Money amount={value} decimals={0} className="whitespace-nowrap" />}
      </div>
      <div className="relative mt-1 text-caption text-cool">{hint}</div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border border-cobalt/20 bg-cobalt/10 px-3 py-2 text-center text-cobalt">
      <div className="text-display-sm font-semibold" data-numeric>{value}</div>
      <div className="text-nano font-medium uppercase tracking-[0.12em] opacity-75">{label}</div>
    </div>
  );
}
